import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Car,
  FileText,
  Calendar,
  Compass,
  BarChart2,
  LineChart,
  Wallet,
  UserSearch,
  Sparkles,
  ShieldCheck,
  ArrowRight,
  TrendingUp,
  Database,
  Download,
} from "lucide-react";
import "../styles/AdminDashBoard.css";
import ResponsiveAppBar from "./AppBar";
import Footer from "./Footer";
import DatabaseExcelExportModal from "./DatabaseExcelExportModal";

const cards = [
  {
    title: "Gold & Vehicle Loans",
    category: "Loan Operations",
    icon: Car,
    desc: "Review and manage gold and vehicle loan applications, verify customer details, and track approvals.",
    path: "/admin",
    button: "Manage Loans",
    badge: "Applications",
    color: "#77c8c7",
  },
  {
    title: "Velavan Finance",
    category: "Ledger Management",
    icon: FileText,
    desc: "Comprehensive company loan database, customer records, loan disbursements, and account entries.",
    path: "/velavan-finance",
    button: "View Finance Ledger",
    badge: "Core Ledger",
    color: "#936097",
  },
  {
    title: "Due Matters",
    category: "Collections & Dues",
    icon: Calendar,
    desc: "Track weekly dues, overdue repayments, upcoming collection targets, and customer balance breakdowns.",
    path: "/due-matters",
    button: "Manage Dues",
    badge: "Collections",
    color: "#77c8c7",
  },
  {
    title: "Interest Calculations",
    category: "Financial Metrics",
    icon: Wallet,
    desc: "Monitor principal vs interest recovery, track cumulative earnings, pending dues, and overall profit calculations.",
    path: "/interestCalculation",
    button: "Calculate Interest",
    badge: "Analytics",
    color: "#936097",
  },
  {
    title: "Daily Accounts",
    category: "Daily Bookkeeping",
    icon: Compass,
    desc: "Track daily fund releases, collections, refreshment & doc charges, and real-time closing balances.",
    path: "/dailyAccounts",
    button: "Daily Records",
    badge: "Day Book",
    color: "#77c8c7",
  },
  {
    title: "Final Accounts",
    category: "Yearly Audit",
    icon: BarChart2,
    desc: "Consolidate daily books into annual financial summaries, comprehensive yearly reports, and audit trails.",
    path: "/finalAccounts",
    button: "Annual Summary",
    badge: "Yearly Audit",
    color: "#936097",
  },
  {
    title: "Monthly Graph Analytics",
    category: "Visual Trends",
    icon: LineChart,
    desc: "Interactive visual charts showing monthly fund flows, loan disbursements, collections, and trend forecasts.",
    path: "/monthly-graph",
    button: "View Chart Analytics",
    badge: "Reports",
    color: "#77c8c7",
  },
  {
    title: "Account Status",
    category: "Customer Portal",
    icon: UserSearch,
    desc: "Quick search for individual customer accounts, active status verification, and instant balance alerts.",
    path: "/account-status",
    button: "Check Status",
    badge: "Lookup",
    color: "#936097",
  },
  {
    title: "Database Excel Export",
    category: "Full Backup & Export",
    icon: Database,
    desc: "Extract all 6 database collections into a single multi-sheet Excel file (.xlsx) with separate tabs for Velavan Finance, Due Matters, Interest, Daily, Final & Account Status.",
    isExportAction: true,
    button: "Export All to Excel (.xlsx)",
    badge: "6-in-1 Excel",
    color: "#0f766e",
    style: "featured-export-card",
  },
];

const AdminDashBoard = () => {
  const navigate = useNavigate();
  const [openExportModal, setOpenExportModal] = useState(false);
  const sessionUser = JSON.parse(sessionStorage.getItem("user") || "{}");
  const adminName = sessionUser.name || "Administrator";
  const userRole = sessionUser.role || "Admin Manager";

  const currentDate = new Date().toLocaleDateString("en-IN", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const handleCardClick = (card) => {
    if (card.isExportAction) {
      setOpenExportModal(true);
    } else {
      navigate(card.path);
    }
  };

  return (
    <div className="admin-portal-wrapper">
      <ResponsiveAppBar />

      {/* Hero Welcome Header */}
      <section className="admin-hero-banner">
        <div className="admin-hero-container">
          <div className="admin-hero-content">
            <div className="admin-hero-badge">
              <Sparkles size={16} className="badge-sparkle-icon" />
              <span>Executive Management Suite</span>
              <span className="badge-dot" />
              <span className="badge-role">{userRole}</span>
            </div>

            <h1 className="admin-hero-title">
              Welcome back, <span className="admin-name-highlight">{adminName}</span>
            </h1>

            <p className="admin-hero-subtitle">
              Monitor loan disbursements, collection dues, ledger entries, and monthly accounts from your centralized portal.
            </p>
          </div>

          <div className="admin-hero-meta">
            <div className="admin-date-badge">
              <Calendar size={18} className="meta-icon" />
              <div>
                <span className="meta-label">Today's Date</span>
                <span className="meta-value">{currentDate}</span>
              </div>
            </div>

            <div className="admin-security-badge">
              <ShieldCheck size={18} className="meta-icon-green" />
              <div>
                <span className="meta-label">System Security</span>
                <span className="meta-value-green">Active • Verified</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Cards Grid */}
      <main className="dashboard-main-content">
        <div className="dashboard-grid-header">
          <div>
            <h2 className="section-heading">Finance Management Modules</h2>
            <p className="section-subheading">Select a module to review records, manage accounts, or export full database reports</p>
          </div>
          <div className="active-modules-count">
            <TrendingUp size={16} />
            <span>{cards.length} Active Modules</span>
          </div>
        </div>

        <div className="dashboard-cards-grid">
          {cards.map((card, index) => {
            const Icon = card.icon;
            return (
              <div
                key={index}
                className={`admin-feature-card ${card.style || ""}`}
                style={{ animationDelay: `${index * 0.08}s` }}
                onClick={() => handleCardClick(card)}
              >
                <div className="card-top-bar">
                  <div className="icon-wrapper" style={{ borderColor: `${card.color}30`, backgroundColor: `${card.color}15` }}>
                    <Icon size={24} style={{ color: card.color }} />
                  </div>
                  <span className="category-pill" style={{ color: card.color, backgroundColor: `${card.color}12`, borderColor: `${card.color}25` }}>
                    {card.badge}
                  </span>
                </div>

                <div className="card-body">
                  <span className="module-category-label">{card.category}</span>
                  <h3 className="module-card-title">{card.title}</h3>
                  <p className="module-card-desc">{card.desc}</p>
                </div>

                <div className="card-footer">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCardClick(card);
                    }}
                    className="module-action-btn"
                  >
                    <span>{card.button}</span>
                    {card.isExportAction ? (
                      <Download size={16} className="btn-arrow" />
                    ) : (
                      <ArrowRight size={16} className="btn-arrow" />
                    )}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </main>

      {/* Database Excel Export Modal */}
      <DatabaseExcelExportModal
        open={openExportModal}
        onClose={() => setOpenExportModal(false)}
      />

      <Footer />
    </div>
  );
};

export default AdminDashBoard;